import { useEffect, useState, useRef, useContext } from "react";
import { useAsync } from "../../utilities/asyncFunc";
import {
  createOrUpdateOrder,
  setOngoing,
  setStatus
} from "../../state_management/epics";
import { getDepositAmount } from "../../state_management/epics/order";
import { orderDetailsType } from "../../state_management/actionTypes";
import { orderInfo, orderType } from "../../state_management/reducers/order";
import moment from "moment";
import {
  PaymentIntent,
  PaymentIntentResult,
  StripePaymentElementChangeEvent
} from "@stripe/stripe-js";
import {
  add_payment_info_ga,
  ga_params_type,
  purchase_ga
} from "../../utilities/analytics";
import { coupon_error_message, getCurrency } from "../../utilities/misc";
import { total } from "./PaymentDirect";
import { getCookie } from "../../utilities/cookieManager";
import { initialState as orderInitialState } from "../../state_management/reducers/order";
import {
  couponErrorTypeArray,
  couponErrorTypeEnum
} from "../../utilities/useCheckoutCoupon";
import { LocaleContext } from "../../AppProvider";

export const parseDirectOrder = (
  details: orderDetailsType["data"] | undefined
): orderType => {
  if (details) {
    return {
      cleaner_id: details.cleaner_id,
      //still waiting
      random_cleaner: !!details.random_cleaner,
      client_address: details.address?.address || "",
      client_email: details.customer.email,
      client_firstname: details.customer.firstname,
      client_lastname: details.customer.lastname,
      client_mobile: details.customer.mobile,
      eircode: details.address?.eircode || undefined,
      coupon_code: details["coupon code"],
      full_payment: !!details.full_payment,
      info: details.info,
      latitude: details.address?.latitude ?? null,
      longitude: details.address?.longitude ?? null,
      time_start: moment(details.time_start).format("YYYY-MM-DD HH:mm")
    };
  } else {
    return orderInitialState.details;
  }
};

type defaultTypes = {
  orderErrorCallback?: (
    couponErrorMessage: couponErrorTypeEnum | undefined
  ) => void;
  services: {
    id: number;
    quantity: number;
    is_upsell: boolean;
  }[];
  extra_services: {
    id: number;
    parent_id: number;
    quantity: number;
  }[];
  details: orderType;
  gift_values: { service_id: number; quantity: number; value: number }[];
  cart: ga_params_type["items"];
  extras: ga_params_type["items"];
  orderInfo: orderInfo;
  setOrderInfo: (data: orderInfo) => void;
  // Others
  clientSecret: string | undefined;
  Total: number;
  setError: (data: string | undefined) => void;
  setDisabled: (data: boolean) => void;
  setProcessing: (data: boolean) => void;
  setPaymentSuccess: (data: boolean) => void;

  startSubmit: () => void;
  successOnSubmit: () => void;
};

const usePayment = ({
  details,
  services,
  extra_services,
  gift_values,
  cart,
  extras,
  isDirect,
  orderInfo,
  setOrderInfo,
  orderErrorCallback,
  setDetails,
  //others
  clientSecret,
  Total,
  setError,
  setDisabled,

  setProcessing,
  setPaymentSuccess,

  startSubmit,
  successOnSubmit
}: defaultTypes &
  (
    | {
        setDetails: (data: orderType) => void;
        isDirect?: never;
      }
    | {
        setDetails?: never;
        isDirect: true;
      }
  )) => {
  const { t } = useContext(LocaleContext);
  /**
   * For manipulating the deposit type
   * The deposit amount is store in a value here
   * The function to handle the payment type is exposed
   * The payment handler is also exposed
   * Ancilliary functions are included via from external components
   */

  const by_staff = Boolean(getCookie("cleanbee_secretary"));
  const [orderDetails, setOrderDetails] = useState<orderType>(details);
  const [depositValue, setDepositValue] = useState<number>();
  const [paymentRequired, setPaymentRequired] = useState(true);
  const [full_payment, setFullPayment] = useState(!!details.full_payment);

  const paymentTypeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    let isFull = e.target.value === "fullType" ? true : false;
    if (full_payment === !isFull) {
      /**
       * We are maually executing for direct payment because changes to normal
       * payments will automatically send requests via the useEffect
       * Unlike direct payments that have an immediate set to false
       */
      if (isDirect) {
        createOrUpdateOrderRes.execute({
          by_staff,
          orderDetails: { ...orderDetails, full_payment: isFull },
          orderInfo,
          gift_values,
          extra_services,
          services
        });
      }
    }

    setFullPayment(isFull);
  };

  useEffect(() => {
    setOrderDetails({ ...details });
  }, [JSON.stringify(details)]);

  // Order details setting for full payment
  useEffect(() => {
    if (orderDetails.full_payment !== full_payment) {
      const newDetails = {
        ...orderDetails,
        full_payment: full_payment
      };
      if (setDetails) {
        setDetails({ ...newDetails });
      }
      setOrderDetails({ ...newDetails });
    }
  }, [full_payment]);

  const depositParams = {
    order_id: orderInfo?.orderId || undefined,
    cleaner_id: orderInfo?.cleaner_id || undefined,
    coupon_code: orderDetails.coupon_code,
    full_payment: false,
    random_cleaner: !!orderDetails.random_cleaner,
    extra_services: extra_services,
    services: services,
    gift_values
  };
  const depositRes = useAsync(
    {
      asyncFunc: getDepositAmount,
      funcParams: {
        ...depositParams
      },
      immediate: true,
      clearOnError: true
    },
    [JSON.stringify(depositParams)]
  );

  useEffect(() => {
    const deposit = depositRes.data;
    setDepositValue(deposit);
  }, [depositRes.data]);

  const createOrUpdateOrderRes = useAsync(
    {
      asyncFunc: createOrUpdateOrder,
      funcParams: {
        by_staff,
        orderDetails,
        orderInfo,
        gift_values,
        extra_services: extra_services,
        services: services
      },
      immediate: !isDirect,
      clearOnError: true
    },
    [JSON.stringify(orderDetails)]
  );

  useEffect(() => {
    const orderInfo = createOrUpdateOrderRes?.data;
    if (orderInfo) {
      const { client_secret, order, payment_required } = orderInfo;
      setPaymentRequired(payment_required);
      setOrderInfo({
        clientSecret: payment_required ? client_secret : undefined,
        orderId: order.id,
        status: order.status,
        time_start: order.time_start,
        cleaner_id: order.cleaner_id,
        random_cleaner: !!orderDetails.random_cleaner
      });
    }
  }, [createOrUpdateOrderRes.data]);

  useEffect(() => {
    if (createOrUpdateOrderRes.error) {
      const errorObject = createOrUpdateOrderRes.errorObject;
      const errorType = (errorObject as null | { type?: couponErrorTypeEnum })
        ?.type;
      const isValidCouponErrorType =
        errorType && couponErrorTypeArray.indexOf(errorType) >= 0;
      const couponErrorMessage = isValidCouponErrorType
        ? coupon_error_message(errorType, t)
        : undefined;
      setError(couponErrorMessage);

      if (orderErrorCallback)
        orderErrorCallback(isValidCouponErrorType ? errorType : undefined);
      setPaymentRequired(true);
    }
  }, [createOrUpdateOrderRes.error]);

  // HANDLING OTHERS

  // Set payment intent status message
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const setOngoingRes = useAsync(
    {
      asyncFunc: setOngoing,
      funcParams: {
        id: orderInfo?.orderId
      },
      immediate: false,
      clearOnError: true
    },
    []
  );

  const setStatusRes = useAsync({
    asyncFunc: setStatus,
    funcParams: { id: orderInfo?.orderId, status: orderInfo?.status },
    canCall: !!orderInfo?.orderId,
    immediate: false,
    clearOnError: true
  });

  const parseCart: ga_params_type["items"] = [
    ...cart.map((item) => ({
      id: item.id,
      type: item.type,
      quantity: item.quantity,
      item: {
        name: item.item.name,
        price_with_discount: item.item.price_with_discount
      },
      extra: false
    })),
    ...extras.map((item) => ({
      id: item.id,
      type: item.type,
      item: {
        name: item.item.name,
        price_with_discount: item.item.price_with_discount
      },
      extra: true,
      quantity: item.quantity
    }))
  ];

  const processPaymentHooks = {
    handlePaymentIntentRetrieval(paymentIntent: PaymentIntent | undefined) {
      if (mountedRef) {
        if (paymentIntent?.status === "succeeded") {
          setError(undefined);
          setProcessing(false);
          setPaymentSuccess(true);
        } else if (paymentIntent?.status === "processing") {
          setError(undefined);
          setProcessing(true);
          setPaymentSuccess(false);
        }
      }
    },
    async startSubmit() {
      setProcessing(true);
      await setOngoingRes.execute(
        {
          id: orderInfo?.orderId
        },
        { throws: true }
      );
    },
    async start() {
      if (isDirect) {
        await createOrUpdateOrderRes.execute(
          {
            by_staff,
            orderDetails,
            orderInfo,
            gift_values,
            extra_services,
            services
          },
          { throws: true }
        );
      }

      startSubmit();
      setError(undefined);
    },
    done(
      payload: PaymentIntentResult,
      handlerType: "card" | "express_checkout"
    ) {
      if (payload.error) {
        setError(
          `${t("Payment failed")}: ${
            payload.error.message || "An unexpected error occurred."
          }`
        );
        setStatusRes.execute({
          id: orderInfo?.orderId,
          status: orderInfo?.status
        });
        setProcessing(false);
      } else {
        successOnSubmit();
        purchase_ga({
          coupon: orderDetails.coupon_code,
          currency: getCurrency(),
          transaction_id: orderInfo?.orderId,
          items: parseCart,
          value: depositValue!,
          payment_type:
            handlerType === "card" ? "Credit Card" : "Apple/Google Pay"
        });

        setError(undefined);
        setProcessing(false);
      }
    },
    error(e: unknown) {
      console.error(e);
      // Reset status
      setStatusRes.execute({
        id: orderInfo?.orderId,
        status: orderInfo?.status
      });
      const type = (Object(e).type as couponErrorTypeEnum) || undefined;
      const errorMessage =
        type && couponErrorTypeArray.indexOf(type) >= 0
          ? coupon_error_message(type, t)
          : undefined;

      setError(
        errorMessage
          ? errorMessage
          : `Something went wrong while processing the payment: ${t(
              "Payment failed"
            )}`
      );
      setProcessing(false);
    },
    handleChange(event: StripePaymentElementChangeEvent) {
      const isComplete = event.complete;
      if (isComplete) {
        add_payment_info_ga({
          coupon: orderDetails.coupon_code,
          currency: getCurrency(),
          items: parseCart,
          value: depositValue!,
          payment_type: "Credit Card"
        });
      }

      setDisabled(!isComplete);
    }
  };

  const notNullOrUndefined = (value: any) => {
    return value !== null && value !== undefined;
  };

  const loading = createOrUpdateOrderRes.loading;

  const TotalDeposit = total(depositValue, 0);

  const showContent = () => {
    return (
      (isDirect ? true : !loading) &&
      !!notNullOrUndefined(Total) &&
      !!notNullOrUndefined(TotalDeposit) &&
      !!clientSecret
    );
  };

  return {
    paymentTypeHandler,
    setPaymentRequired,
    full_payment,
    createOrUpdateOrderRes,
    depositRes,
    paymentRequired,
    depositValue,
    TotalDeposit,
    showContent,
    processPaymentHooks
  };
};

export default usePayment;

export type usePayType = ReturnType<typeof usePayment>;
